import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { isFalsy } from 'utility-types';
import { ArchivosTemporalesService } from '@services/archivos-temporales/archivos-temporales.service';
import { SnapshotComprobanteService } from '@services/snapshot-comprobante/snapshot-comprobante.service';

export interface ComprobantePdf {
  contenidoBase64: string;
  nombreArchivo: string;
  /** true si hubo que reconstruirlo porque el archivo original ya no estaba */
  reconstruido: boolean;
}

/**
 * Resuelve el PDF de un comprobante en un solo paso.
 *
 * Primero busca el archivo original (ArchivosTemporal). Si ya no está —se borran
 * por antigüedad— lo reconstruye desde el respaldo. Así, quien necesite el PDF
 * (descargar, compartir, adjuntar) no tiene que repetir esa lógica.
 */
@Injectable({
  providedIn: 'root'
})
export class ComprobantePdfService {

  constructor(
    private archivosTemporalesService: ArchivosTemporalesService,
    private snapshotComprobanteService: SnapshotComprobanteService
  ) { }

  obtenerPdf(idComprobante: number): Observable<ComprobantePdf> {
    return this.archivosTemporalesService.getAllByIdComprobante(idComprobante).pipe(
      switchMap(archivos => {
        if (isFalsy(archivos) || archivos.length === 0)
          return this.reconstruir(idComprobante)

        const archivo = archivos[0]

        return this.archivosTemporalesService.getFileContentById(archivo.id).pipe(
          switchMap(data => {
            if (isFalsy(data) || isFalsy(data.content))
              return this.reconstruir(idComprobante)

            const nombre = isFalsy(archivo.nombreArchivo) ? `Comprobante_${idComprobante}` : archivo.nombreArchivo.trim()

            return of({
              contenidoBase64: data.content,
              nombreArchivo: nombre.toLowerCase().endsWith('.pdf') ? nombre : `${nombre}.pdf`,
              reconstruido: false
            })
          }),
          catchError(() => this.reconstruir(idComprobante))
        )
      }),
      catchError(() => this.reconstruir(idComprobante))
    )
  }

  private reconstruir(idComprobante: number): Observable<ComprobantePdf> {
    return this.snapshotComprobanteService.reconstruirPdf(idComprobante).pipe(
      map(data => {
        if (isFalsy(data) || isFalsy(data.content))
          throw new Error('Sin respaldo')

        return {
          contenidoBase64: data.content,
          nombreArchivo: isFalsy(data.fileName) ? `Comprobante_${idComprobante}.pdf` : data.fileName,
          reconstruido: true
        }
      }),
      catchError(() => throwError(() => new Error(
        'No hay archivo ni respaldo disponible para este comprobante.')))
    )
  }

  /** Convierte el base64 en un File, que es lo que necesita el compartir nativo */
  base64AFile(contenidoBase64: string, nombreArchivo: string): File {
    const binario = atob(contenidoBase64)
    const bytes = new Uint8Array(binario.length)

    for (let i = 0; i < binario.length; i++)
      bytes[i] = binario.charCodeAt(i)

    return new File([bytes], nombreArchivo, { type: 'application/pdf' })
  }
}
